<template>
  <div class="file-viewer" :style="containerStyle">
    <NotificationBar
      v-if="error"
      severity="error"
      :mainText="error"
      icon="mdi mdi-file-question"
      class="m-4"
      :showCloseButton="false"
    />

    <!-- Inline image -->
    <img
      v-else-if="canRenderInline && !loading && previewKind === 'image' && blobUrl"
      :src="blobUrl"
      :class="['max-w-full h-auto block', { 'opacity-0': viewerPending }]"
      alt=""
      @load="onInlineLoaded"
      @error="onInlineLoaded"
    />

    <!-- Inline PDF or text via iframe -->
    <iframe
      v-else-if="
        canRenderInline && !loading && (previewKind === 'pdf' || previewKind === 'text') && blobUrl
      "
      :src="blobUrl"
      :class="['w-full h-[80vh] border-0', { 'opacity-0': viewerPending }]"
      @load="onInlineLoaded"
    ></iframe>

    <iframe
      v-else-if="canRenderInline && !loading && blobUrl"
      :src="blobUrl"
      :class="['w-full h-screen', { 'opacity-0': viewerPending }]"
      @load="onInlineLoaded"
    ></iframe>

    <InlineSpinner
      v-else-if="loading"
      class="file-viewer-loading"
      :active="true"
      text="messages.loadingPDF"
    />

    <InlineSpinner
      v-else-if="!canRenderInline && !error"
      class="file-viewer-loading"
      :active="true"
      text="messages.loadingPDF"
    />

    <InlineSpinner
      v-if="viewerPending && !loading && !error"
      class="file-viewer-loading-overlay"
      :active="true"
      text="messages.loadingPDF"
      aria-live="polite"
    />

    <!-- Unsupported inline: show manual download button -->
    <div class="p-4 flex items-center gap-3">
      <div class="flex-1">
        <NotificationBar
          v-if="previewKind === 'unsupported' && !loading"
          :mainText="$t('messages.noInlinePreview')"
          icon="pi pi-eye-slash"
          :showCloseButton="false"
        />
        <!-- <i class="pi pi-file"></i> -->
        <Button
          v-if="!error && !loading"
          icon="pi pi-download"
          class="mt-2"
          :label="
            props.filename
              ? $t('buttons.downloadFileWithName', { name: props.filename })
              : $t('buttons.downloadFile')
          "
          @click="downloadManually"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { getPreviewKind, type PreviewKind } from '@/utils/files';
import { DocumentsService } from '@/openapi/services/DocumentsService';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    uuid: string;
    mime?: string;
    filename?: string;
    allowText?: boolean;
    allowCsvAsText?: boolean;
    initialHeight?: string;
  }>(),
  {
    mime: undefined,
    filename: undefined,
    initialHeight: '1000px',
  },
);

const blobValue = ref<Blob | null>(null);
const blobUrl = ref('');
const loading = ref(false);
const viewerPending = ref(false);
const error = ref('');
const previewKind = ref<PreviewKind>('unsupported');
const pageReady = ref<boolean>(
  typeof document === 'undefined' || document.readyState === 'complete',
);
const pageReadyBypass = ref(false);
let pageReadyFallbackTimer: ReturnType<typeof setTimeout> | null = null;

const isBlobLoaded = computed(() => !!blobUrl.value);
const canRenderInline = computed(() => pageReady.value || pageReadyBypass.value);

/** Height logic based purely on status (blob/error/unsupported/loading). */
const containerStyle = computed(() => {
  // Error or unsupported preview -> auto (no need for reserved space)
  if (error.value) return { height: 'auto' };
  if (!loading.value && previewKind.value === 'unsupported') return { height: 'auto' };

  // Keep reserved space while inline rendering is intentionally deferred.
  if (!canRenderInline.value) return { height: props.initialHeight };

  // Keep reserved space while renderer is still initializing.
  if (viewerPending.value) return { height: props.initialHeight };

  // Blob fetched -> we can release reserved height
  if (isBlobLoaded.value) return { height: 'auto' };

  // Otherwise (initial / loading / pre-fetch) keep reserved height
  return { height: props.initialHeight };
});

watch(
  () => [props.uuid, props.mime],
  async ([uuid, mime]) => {
    // Only fetch blob for inline-previewable types
    if (uuid) {
      await loadBlobForPreview(uuid, mime);
    } else {
      // prevent auto-download for unsupported: do nothing
      revokeUrl();
    }
  },
  { immediate: true },
);

async function loadBlobForPreview(uuid: string, mime?: string) {
  loading.value = true;
  viewerPending.value = false;
  error.value = '';
  revokeUrl();

  try {
    const blob = await DocumentsService.documentControllerV1DownloadDocumentV1(uuid);
    // Rely on server content-type so the browser knows how to render
    blobValue.value = blob;

    previewKind.value = await getPreviewKind(
      mime,
      {
        allowText: props.allowText,
        allowCsvAsText: props.allowCsvAsText,
      },
      blob,
    );

    if (previewKind.value !== 'unsupported') {
      viewerPending.value = true;
      // Convert octet-stream to PDF for preview
      let blobForPreview = blob;
      if (mime === 'application/octet-stream' && previewKind.value === 'pdf') {
        blobForPreview = new Blob([blob], { type: 'application/pdf' });
      }
      blobUrl.value = URL.createObjectURL(blobForPreview);
    } else {
      viewerPending.value = false;
    }
  } catch (err: any) {
    viewerPending.value = false;
    if (err?.response?.status === 404) {
      error.value = t('toast.errors.FILE_NOT_FOUND');
    } else {
      error.value = t('toast.errors.FILE_DOWNLOAD_ERROR');
    }
  } finally {
    loading.value = false;
  }
}

function revokeUrl() {
  if (blobUrl.value) {
    URL.revokeObjectURL(blobUrl.value);
    blobUrl.value = '';
  }
  viewerPending.value = false;
}

function onInlineLoaded() {
  viewerPending.value = false;
}

function onPageLoaded() {
  pageReady.value = true;
  pageReadyBypass.value = false;
  if (pageReadyFallbackTimer) {
    clearTimeout(pageReadyFallbackTimer);
    pageReadyFallbackTimer = null;
  }
}

/** Manual download on user action only (no auto-download). */
async function downloadManually() {
  try {
    loading.value = true;
    const blob = await DocumentsService.documentControllerV1DownloadDocumentV1(props.uuid);
    const url = URL.createObjectURL(blob);
    // trigger a download via temporary anchor
    const a = document.createElement('a');
    a.href = url;
    a.download = props.filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (_err) {
    error.value = t('toast.errors.FILE_DOWNLOAD_ERROR');
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (typeof window === 'undefined' || pageReady.value) return;

  // Safety net: if the browser load event is missed, never block inline rendering forever.
  pageReadyFallbackTimer = setTimeout(() => {
    pageReadyBypass.value = true;
  }, 1500);

  window.addEventListener('load', onPageLoaded, { once: true });
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('load', onPageLoaded);
  }
  if (pageReadyFallbackTimer) {
    clearTimeout(pageReadyFallbackTimer);
    pageReadyFallbackTimer = null;
  }
  revokeUrl();
});
</script>

<style lang="css">
@layer app {
  iframe {
    border: none;
  }

  .file-viewer {
    position: relative;
    display: block;
    overflow: auto;
  }

  .file-viewer-loading,
  .file-viewer-loading-overlay {
    min-height: 12rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
  }

  .file-viewer-loading-overlay {
    position: absolute;
    inset: 0;
    background: color-mix(in srgb, var(--p-surface-0, #ffffff) 82%, transparent);
    z-index: 1;
  }
}
</style>
